import { useState, useEffect, useCallback } from "react";

export interface InstallPromptState {
  /** Whether the install prompt is available to show */
  isPromptAvailable: boolean;
  /** Whether the app has already been installed */
  isInstalled: boolean;
  /** Trigger the install prompt */
  promptInstall: () => Promise<void>;
  /** Dismiss the install prompt for this session */
  dismissPrompt: () => void;
}

/**
 * Hook that manages the PWA install prompt lifecycle.
 *
 * Listens for the `beforeinstallprompt` event (fired by browsers that
 * support PWA installation), stores the event, and provides a
 * `promptInstall()` function to trigger it.
 *
 * Once the user installs the app or dismisses the prompt, the banner
 * is hidden for the session.
 */
export function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode (already installed)
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    ) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e: Event) => {
      // Prevent the mini-infobar from appearing automatically
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    setDeferredPrompt(null);

    if (result.outcome === "accepted") {
      setIsInstalled(true);
    }
  }, [deferredPrompt]);

  const dismissPrompt = useCallback(() => {
    setDismissed(true);
  }, []);

  return {
    isPromptAvailable: deferredPrompt !== null && !dismissed,
    isInstalled,
    promptInstall,
    dismissPrompt,
  };
}

// Type declaration for the non-standard BeforeInstallPromptEvent
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt: () => Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
    appinstalled: Event;
  }
}
